import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";
import { formatKes } from "lib/format";
import PlacementPicker from "components/admin/PlacementPicker";
import { saveDraft, loadDraft, clearDraft, isWorthSaving, describeAge, saveServerDraft } from "./draftRecovery";

/** Same five-timestamp defaulting as the admin Create Market form (see
 *  superadmin-portal/src/views/admin/markets) — kept in sync by hand since the two
 *  portals are separate CRA apps with no shared component package (see
 *  docs/architecture/01-monorepo-structure.md). */
function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/** Rank of the administrative level a Kenya 2027 race is contested at. A market tagged
 *  with several races has to satisfy the deepest one. */
const KE2027_LEVEL_RANK = { national: 0, county: 1, constituency: 2 };

/**
 * Placement rules, mirrored from the server so the creator is told at step one rather
 * than at submit. Two of them:
 *
 *  - At least one cluster. A market with none is reachable only from search, which is
 *    almost never what the creator meant.
 *  - Kenya 2027 races are contested in a specific administrative unit, and the elections
 *    hub matches markets to the map by exactly that unit (placement.unit.county). A
 *    governor market saved with no county is not merely untidy — it is unroutable, and
 *    silently never appears on the hub it was created for.
 *
 * Cluster ids here are numeric (the reference implementation this came from used string
 * ids like 'cluster-ke2027'), so ke2027 is identified by its keyName, not a literal id.
 */
function validatePlacement(form, clusters, ke2027) {
  const errors = {};
  if (!form.clusterIds.length) {
    errors.clusterIds = "Choose at least one cluster, or the market is only reachable from search.";
    return errors;
  }

  const ke2027Cluster = (clusters || []).find((c) => c.keyName === "ke2027");
  if (!ke2027Cluster || !form.clusterIds.includes(ke2027Cluster.id)) return errors;

  const races = ((ke2027 && ke2027.races) || []).filter((r) => form.tagIds.includes(r.id));
  if (!races.length) {
    errors.tagIds = "Pick the race this market settles on, so it lands on the right tab of the Kenya 2027 hub.";
    return errors;
  }

  let level = "national";
  let unitOptional = true;
  races.forEach((r) => {
    if (KE2027_LEVEL_RANK[r.level] > KE2027_LEVEL_RANK[level]) level = r.level;
    if (!r.unitOptional && r.level !== "national") unitOptional = false;
  });

  if (level !== "national" && !unitOptional) {
    if (!form.county) errors.county = "Choose the county this race is contested in.";
    if (level === "constituency" && !form.constituency) errors.constituency = "Choose the constituency.";
  }
  return errors;
}

/** Mirrors packages/contracts/src/markets.ts's createMarketRequestSchema and
 *  marketService.assertValidTiming exactly — every error shown here is one the backend
 *  would otherwise reject with, just surfaced before the round trip instead of after. */
function validateMarketForm(form, clusters, ke2027) {
  const errors = { ...validatePlacement(form, clusters, ke2027) };
  if (!form.question.trim()) errors.question = "A market needs a question.";
  else if (form.question.length > 500) errors.question = "Keep it under 500 characters.";
  if (!form.slug.trim()) errors.slug = "A market needs a URL slug.";
  else if (form.slug.length < 3 || form.slug.length > 160) errors.slug = "3–160 characters.";
  else if (!SLUG_PATTERN.test(form.slug)) errors.slug = "Lowercase letters, numbers and hyphens only — no leading/trailing hyphen.";
  if (!form.description.trim()) errors.description = "Traders will see this — describe what's being asked.";

  const opens = new Date(form.opensAt), auction = new Date(form.auctionStartsAt), cont = new Date(form.continuousStartsAt);
  const closes = new Date(form.closesAt), deadline = new Date(form.settlementDeadlineAt);
  if (!(opens < auction)) errors.auctionStartsAt = "Must be after Opens at.";
  if (!(auction < cont)) errors.continuousStartsAt = "Must be after Auction starts.";
  if (!(cont < closes)) errors.closesAt = "Must be after Continuous trading starts.";
  if (!(closes <= deadline)) errors.settlementDeadlineAt = "Must be at or after Closes at.";

  if (!form.resolutionCriteria.trim()) errors.resolutionCriteria = "How will this be judged? Traders rely on this being unambiguous.";
  if (!form.whatMeasured.trim()) errors.whatMeasured = "Required.";
  if (!form.whenMeasured.trim()) errors.whenMeasured = "Required.";
  if (!form.whereSource.trim()) errors.whereSource = "Required — name the specific source.";
  if (!form.yesCondition.trim()) errors.yesCondition = "Spell out exactly what makes this YES.";
  if (!form.noCondition.trim()) errors.noCondition = "Spell out exactly what makes this NO.";
  return errors;
}

/** `message` shows in red once the field's been touched or Next/submit was attempted;
 *  `fallback` (plain gray helper text) is this form's tooltip equivalent — shown the rest
 *  of the time, matching the pattern already established in this file. */
function FieldError({ message, fallback }) {
  if (message) return <p className="mt-1 text-xs font-medium text-red-500">{message}</p>;
  if (fallback) return <p className="mt-1 text-xs text-gray-400">{fallback}</p>;
  return null;
}

function defaultTiming() {
  const opens = new Date(Date.now() + 60 * 60 * 1000);
  const toLocal = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  return {
    opensAt: toLocal(opens),
    auctionStartsAt: toLocal(new Date(opens.getTime() + 60 * 1000)),
    continuousStartsAt: toLocal(new Date(opens.getTime() + 2 * 60 * 1000)),
    closesAt: toLocal(new Date(opens.getTime() + 7 * 24 * 60 * 60 * 1000)),
    settlementDeadlineAt: toLocal(new Date(opens.getTime() + 8 * 24 * 60 * 60 * 1000)),
  };
}

const STEP_LABELS = ["Your question", "Your earnings", "The cost", "Staying honest", "Confirm"];

function WizardProgress({ step }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {STEP_LABELS.map((label, i) => {
        const isDone = i < step;
        const isCurrent = i === step;
        return (
          <div key={label} className="flex items-center gap-1.5">
            <span
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-300 ${
                isCurrent
                  ? "bg-primary text-primary-foreground"
                  : isDone
                    ? "bg-green-50 text-green-600 dark:bg-green-400/10"
                    : "bg-gray-100 text-gray-400 dark:bg-white/5"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-black/10 text-[10px] dark:bg-white/10">
                {isDone ? "✓" : i + 1}
              </span>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && <span className="text-gray-300 dark:text-white/20">→</span>}
          </div>
        );
      })}
    </div>
  );
}

const input = "w-full rounded-md border border-border px-3 py-2 text-sm outline-none transition-[color,box-shadow] focus:border-ring focus:ring-[3px] focus:ring-ring/50 dark:bg-input/30 text-foreground placeholder:text-muted-foreground";
const label = "mb-1 block text-xs font-medium text-muted-foreground";

/**
 * A partner's own market — created here always lands as MANUAL/partner sourced and
 * owned by their organization (the API injects clientId/source server-side; see
 * POST /portal/markets in apps/api/src/modules/portal/routes.ts). No tenant or client
 * picker: a partner can only ever create inside their own organization.
 *
 * Rebuilt as a 5-step wizard (adapted from 5050markets.com's flow, per the product
 * owner): the question, an honest earnings disclaimer, the real live creation cost,
 * an explanation-only honesty step, then a final confirm. Every KES amount shown here
 * comes from GET /admin/market-creation-policy and GET /payments/wallet at request
 * time — never hardcoded — since an admin can change or waive the policy at any time.
 *
 * A real page at /portal/markets/new rather than a modal. A question picked from the
 * review queue arrives as router state (set by the "from a source" page), not a prop.
 */
export default function CreateMarketPage() {
  const { isPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const seedQuestion = location.state?.seedQuestion || null;
  const onClose = () => navigate("/portal/markets");
  const onCreated = () => navigate("/portal/markets");

  const [step, setStep] = useState(0);
  const [step0Attempted, setStep0Attempted] = useState(false);

  const [clusters, setClusters] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [ke2027, setKe2027] = useState(null);
  const [form, setForm] = useState({
    slug: "",
    question: "",
    description: "",
    clusterIds: [],
    tagIds: [],
    county: null,
    constituency: null,
    partyIds: [],
    competitionId: null,
    homeTeamId: null,
    awayTeamId: null,
    kickoffAt: null,
    teamIds: [],
    visibility: "PUBLIC",
    ...defaultTiming(),
    resolutionCriteria: "",
    whatMeasured: "",
    whenMeasured: "",
    whereSource: "",
    yesCondition: "",
    noCondition: "",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [touched, setTouched] = useState({});
  const errors = validateMarketForm(form, clusters, ke2027);
  const isFormComplete = Object.keys(errors).length === 0;

  const [imageFile, setImageFile] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [imageUploadFailedFor, setImageUploadFailedFor] = useState(null);

  // The real, live cost — fetched once when the wizard opens, never hardcoded. Both
  // requests hit the same platform-wide policy / per-user KES wallet the backend
  // actually charges against (see marketCreationEconomicsService.ts), so what's shown
  // here is exactly what would be charged if submitted right now.
  const [policy, setPolicy] = useState(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyError, setPolicyError] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);

  const [recoverable, setRecoverable] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  /** The DRAFT market this form is writing into. A ref, not state: changing it must not
   *  re-render mid-typing, and nothing on screen reads it. */
  const draftMarketIdRef = useRef(null);
  const draftSaveChainRef = useRef(Promise.resolve());
  const finalizingRef = useRef(false);
  const [draftSavedMessage, setDraftSavedMessage] = useState(null);

  /** A question chosen from the review queue starts the form off. It is still a draft
   *  like any other: the fetched text is a starting point, not a finished market. */
  useEffect(() => {
    if (!seedQuestion) return;
    setForm((prev) => ({
      ...prev,
      question: prev.question || seedQuestion.question || "",
      description: prev.description || seedQuestion.description || "",
      resolutionCriteria: prev.resolutionCriteria || seedQuestion.resolutionCriteria || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedQuestion]);

  useEffect(() => {
    const found = loadDraft();
    if (found && isWorthSaving(found.form)) setRecoverable(found);
  }, []);

  /**
   * Autosave, from the first character typed.
   *
   * Two copies, deliberately. localStorage is instant and survives a refresh even
   * offline. The server copy is the durable one: it puts the half-written market in the
   * markets table as a DRAFT, so it shows in the list and is still there from another
   * device, or after this browser's site data is cleared.
   *
   * The returned market id is held so every later save updates that same row — without
   * it, each keystroke would create another market.
   */
  useEffect(() => {
    if (created || recoverable) return undefined;
    if (!isWorthSaving(form)) return undefined;
    const timer = setTimeout(() => {
      const now = Date.now();
      if (saveDraft(form, step, now)) setSavedAt(now);
      draftSaveChainRef.current = draftSaveChainRef.current.then(async () => {
        if (finalizingRef.current) return;
        const id = await saveServerDraft(apiPost, form, draftMarketIdRef.current);
        if (id) draftMarketIdRef.current = id;
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [form, step, created, recoverable]);

  function restoreDraft() {
    if (!recoverable) return;
    setForm(recoverable.form);
    setStep(recoverable.step);
    setSlugTouched(true);
    setRecoverable(null);
  }

  function discardDraft() {
    clearDraft();
    setRecoverable(null);
    setSavedAt(null);
  }

  async function saveAsDraft() {
    if (draftBusy) return;
    setDraftBusy(true);
    setError(null);
    try {
      await apiPost("/portal/ingestion/drafts", {
        question: form.question,
        description: form.description || null,
        clusterIds: form.clusterIds.length ? form.clusterIds : null,
        tagIds: form.tagIds.length ? form.tagIds : null,
        county: form.county,
        constituency: form.constituency,
        partyIds: form.partyIds.length ? form.partyIds : null,
        closesAt: form.closesAt ? new Date(form.closesAt).toISOString() : null,
        settlementDeadlineAt: form.settlementDeadlineAt
          ? new Date(form.settlementDeadlineAt).toISOString()
          : null,
        resolutionCriteria: form.resolutionCriteria || null,
      });
      clearDraft();
      setDraftSavedMessage("Saved. You can finish it from the review queue whenever you like.");
    } catch (err) {
      setError(err.message || "Couldn't save this draft.");
    } finally {
      setDraftBusy(false);
    }
  }

  function fetchPolicy() {
    setPolicyLoading(true);
    setPolicyError(null);
    apiGet("/admin/market-creation-policy")
      .then((res) => setPolicy(res.policy))
      .catch((err) => setPolicyError(err.message || "Couldn't load the current creation policy."))
      .finally(() => setPolicyLoading(false));
  }

  function fetchWallet() {
    setWalletLoading(true);
    setWalletError(null);
    apiGet("/payments/wallet?currency=KES")
      .then((res) => setWallet(res.wallet))
      .catch((err) => setWalletError(err.message || "Couldn't load your wallet balance."))
      .finally(() => setWalletLoading(false));
  }

  useEffect(() => {
    fetchPolicy();
    fetchWallet();
    apiGet("/portal/clusters")
      .then((res) => setClusters(res.clusters || []))
      .catch(() => setClusters([]));
    // Needed to validate Kenya 2027 placement at step one rather than at submit: the
    // race tag decides whether a county (and constituency) is required.
    apiGet("/portal/ke2027")
      .then((res) => setKe2027(res))
      .catch(() => setKe2027(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Where this question looks like it belongs, worked out while it is being typed.
   *
   *  Debounced, because it runs on every keystroke, and deliberately silent on failure:
   *  a suggestion that does not arrive costs a reviewer nothing, and an error banner for
   *  a hint nobody asked for would be worse than the hint being missing. */
  useEffect(() => {
    const question = form.question.trim();
    if (isPartner || question.length < 8) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(() => {
      apiPost("/portal/ingestion/suggest-clusters", { question, description: form.description || undefined })
        .then((res) => {
          if (!cancelled) setSuggestions(res?.suggestions ?? []);
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.question, form.description, isPartner]);

  useEffect(() => () => { if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl); }, [imagePreviewUrl]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }
  function touch(field) {
    setTouched((t) => (t[field] ? t : { ...t, [field]: true }));
  }
  function setQuestion(value) {
    setForm((f) => ({ ...f, question: value, slug: slugTouched ? f.slug : slugify(value) }));
  }
  function fieldError(field) {
    return (touched[field] || step0Attempted) ? errors[field] : undefined;
  }
  function fieldClass(field) {
    return fieldError(field) ? `${input} border-red-400 focus:border-red-400 dark:border-red-500` : input;
  }
  function pickImage(file) {
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImageFile(file || null);
    setImagePreviewUrl(file ? URL.createObjectURL(file) : null);
  }

  // Creating a market is free: no pool seed and no refundable bond are charged any
  // more, so there is no cost to price and no balance that can be too low.
  const costReady = !policyLoading && !policyError;

  function goBack() {
    if (step === 0) { onClose(); return; }
    setStep((s) => Math.max(0, s - 1));
  }
  function goNext() {
    if (step === 0) {
      setStep0Attempted(true);
      if (!isFormComplete) return;
    }
    setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1));
  }

  async function submit() {
    if (!isFormComplete || busy) return;
    setBusy(true);
    setError(null);
    finalizingRef.current = true;
    try {
      await draftSaveChainRef.current;
      const result = await apiPost("/portal/markets", {
        draftMarketId: draftMarketIdRef.current || undefined,
        slug: form.slug,
        question: form.question,
        description: form.description,
        clusterIds: form.clusterIds,
        tagIds: form.tagIds,
        county: form.county,
        constituency: form.constituency,
        partyIds: form.partyIds,
        competitionId: form.competitionId,
        homeTeamId: form.homeTeamId,
        awayTeamId: form.awayTeamId,
        kickoffAt: form.kickoffAt ? new Date(form.kickoffAt).toISOString() : null,
        teamIds: form.teamIds,
        visibility: form.visibility,
        opensAt: new Date(form.opensAt).toISOString(),
        auctionStartsAt: new Date(form.auctionStartsAt).toISOString(),
        continuousStartsAt: new Date(form.continuousStartsAt).toISOString(),
        closesAt: new Date(form.closesAt).toISOString(),
        settlementDeadlineAt: new Date(form.settlementDeadlineAt).toISOString(),
        minOrderAmount: null,
        maxOrderAmount: null,
        maxMarketExposure: null,
        feeConfigId: null,
        settlementRule: {
          resolutionCriteria: form.resolutionCriteria,
          whatMeasured: form.whatMeasured,
          whenMeasured: form.whenMeasured,
          whereSource: form.whereSource,
          yesCondition: form.yesCondition,
          noCondition: form.noCondition,
        },
      });
      // The image can only be attached once the market exists — a second request right
      // after creation. A failed upload here still leaves a real, usable market behind.
      if (imageFile) {
        try {
          const formData = new FormData();
          formData.append("file", imageFile);
          await apiPost(`/portal/markets/${result.market.id}/image`, formData);
        } catch {
          setImageUploadFailedFor(result.market.question);
          setBusy(false);
          return;
        }
      }
      clearDraft();
      setCreated(result.market);
    } catch (err) {
      finalizingRef.current = false;
      if (err.status === 402) {
        setError("Your balance doesn't cover the creation cost. Add funds to your wallet and try again.");
      } else if (err.message === "Something went wrong") {
        // A generic 500 from this specific call almost always means the charge itself
        // failed server-side after this wizard's own balance check passed (e.g. the
        // balance changed in the moment between the check and the charge) — see this
        // change's own report for the backend gap that keeps a real insufficient-balance
        // failure from ever reaching the client as a clean, distinguishable error today.
        setError("We couldn't finish creating this market — this usually means your balance changed just before charging. Refresh your balance on the previous step and try again.");
      } else {
        setError(err.message || "Failed to create market.");
      }
    } finally {
      setBusy(false);
    }
  }

  const wizardStyle = (
    <style>{`
      @keyframes wizardStepIn { from { opacity: 0; transform: translateX(14px); } to { opacity: 1; transform: translateX(0); } }
      @keyframes wizardCheckPop { 0% { opacity: 0; transform: scale(0.4); } 60% { opacity: 1; transform: scale(1.15); } 100% { opacity: 1; transform: scale(1); } }
      .wizard-step-panel { animation: wizardStepIn 0.25s ease-out; }
      .wizard-success-check { animation: wizardCheckPop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
    `}</style>
  );

  if (imageUploadFailedFor) {
    return (
      <div className="mx-auto mt-3 w-full max-w-md rounded-xl bg-white p-6 shadow-3xl dark:bg-navy-800">
        <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Market created</h4>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          "{imageUploadFailedFor}" was created successfully, but its image failed to upload. You can add one later from the market's detail view.
        </p>
        <div className="mt-6 flex justify-end">
          <button onClick={onCreated} className="h-9 rounded-sm bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Done
          </button>
        </div>
      </div>
    );
  }

  if (created) {
    return (
      <div className="mx-auto mt-3 w-full max-w-md rounded-xl bg-white p-6 text-center shadow-3xl dark:bg-navy-800">
        {wizardStyle}
        <div className="wizard-success-check mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-50 text-3xl text-green-600 dark:bg-green-400/10">
          ✓
        </div>
        <h4 className="mt-4 text-lg font-semibold text-navy-700 dark:text-white">Saved as a draft</h4>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          "{created.question}" is in your Drafts. Submit it for approval any time from the markets list to start the review process — it only
          goes live once approved.
        </p>
        <div className="mt-6 flex justify-center">
          <button onClick={onCreated} className="h-9 rounded-sm bg-primary px-4 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90">
            Go to my markets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-3 w-full max-w-3xl rounded-xl bg-white p-6 shadow-3xl dark:bg-navy-800">
      {wizardStyle}
        <h4 className="text-lg font-semibold text-navy-700 dark:text-white">Create your market</h4>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Goes live once approved — you can edit or cancel it before then.</p>
        <WizardProgress step={step} />

        {recoverable && (
          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              You have an unfinished market from {describeAge(recoverable.savedAt)}
            </p>
            <p className="mt-1 text-xs text-amber-800 dark:text-amber-200/80">
              {recoverable.form.question
                ? `"${recoverable.form.question}"`
                : "No question was written yet."}{" "}
              Any picture you had chosen is not restored.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                className="rounded-sm bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700"
                onClick={restoreDraft}
              >
                Pick up where I left off
              </button>
              <button
                type="button"
                className="rounded-sm border border-amber-400 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 dark:text-amber-200 dark:hover:bg-amber-500/20"
                onClick={discardDraft}
              >
                Start fresh
              </button>
            </div>
          </div>
        )}

        {draftSavedMessage && (
          <div className="mt-4 rounded-xl border border-green-300 bg-green-50 p-3 text-sm text-green-800 dark:border-green-500/40 dark:bg-green-500/10 dark:text-green-200">
            {draftSavedMessage}
          </div>
        )}

        <div key={step} className="wizard-step-panel mt-5">
          {step === 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={label}>Question</label>
                <input
                  className={fieldClass("question")}
                  value={form.question}
                  onBlur={() => touch("question")}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Will Kenya's inflation rate exceed 8% in March 2026?"
                />
                <FieldError
                  message={fieldError("question")}
                  fallback="Choose a yes-or-no question with a deadline and a source that can verify the result. Name the exact competition/season rather than saying 'this season'."
                />
              </div>
              <div className="col-span-2">
                <PlacementPicker
                  clusters={clusters}
                  clusterIds={form.clusterIds}
                  tagIds={form.tagIds}
                  county={form.county}
                  constituency={form.constituency}
                  partyIds={form.partyIds}
                  competitionId={form.competitionId}
                  homeTeamId={form.homeTeamId}
                  awayTeamId={form.awayTeamId}
                  kickoffAt={form.kickoffAt}
                  teamIds={form.teamIds}
                  visibility={form.visibility}
                  onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
                  previewPath="/portal/placement/preview"
                  ke2027RefPath="/portal/ke2027"
                  createPartyPath="/portal/ke2027/parties"
                  footballRefPath="/portal/football"
                  createTeamPath="/portal/football/teams"
                  suggestions={suggestions}
                  error={
                    step0Attempted
                      ? errors.clusterIds || errors.tagIds || errors.county || errors.constituency
                      : undefined
                  }
                />
              </div>
              <div className="col-span-2">
                <label className={label}>Slug</label>
                <input
                  className={fieldClass("slug")}
                  value={form.slug}
                  onBlur={() => touch("slug")}
                  onChange={(e) => { setSlugTouched(true); set("slug", e.target.value); }}
                  placeholder="will-x-happen-by-y"
                />
                <FieldError message={fieldError("slug")} fallback="This becomes the market's URL — auto-filled from the question, editable." />
              </div>
              <div className="col-span-2">
                <label className={label}>Description</label>
                <textarea
                  className={fieldClass("description")}
                  rows={2}
                  value={form.description}
                  onBlur={() => touch("description")}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="One or two sentences a trader would read before trading on this."
                />
                <FieldError message={fieldError("description")} fallback="Give the context, not just a repeat of the question above." />
              </div>
              <div className="col-span-2">
                <label className={label}>Image (optional)</label>
                <div className="flex items-center gap-3">
                  {imagePreviewUrl && (
                    <img src={imagePreviewUrl} alt="" className="h-16 w-16 rounded-lg border border-gray-200 object-cover dark:border-white/10" />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(e) => pickImage(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-gray-100 file:px-3 file:py-2 file:text-xs file:font-bold file:text-navy-700 dark:text-gray-300 dark:file:bg-navy-700 dark:file:text-white"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">JPEG, PNG, WebP or GIF, up to 5MB. Shown with the market wherever it's listed.</p>
                  </div>
                  {imageFile && (
                    <button type="button" onClick={() => pickImage(null)} className="text-xs font-semibold text-gray-500 hover:text-red-500">
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className={label}>Visibility</label>
                <select className={input} value={form.visibility} onChange={(e) => set("visibility", e.target.value)}>
                  <option value="PUBLIC">Public</option>
                  <option value="UNLISTED">Unlisted</option>
                </select>
                <FieldError fallback="Public markets appear in every trader's market list. Unlisted ones are only reachable by direct link." />
              </div>
              <div />
              <div>
                <label className={label}>Opens at</label>
                <input type="datetime-local" className={input} value={form.opensAt} onChange={(e) => set("opensAt", e.target.value)} />
                <FieldError fallback="When trading opens for this market." />
              </div>
              <div>
                <label className={label}>Auction starts</label>
                <input
                  type="datetime-local"
                  className={fieldClass("auctionStartsAt")}
                  value={form.auctionStartsAt}
                  onBlur={() => touch("auctionStartsAt")}
                  onChange={(e) => set("auctionStartsAt", e.target.value)}
                />
                <FieldError message={fieldError("auctionStartsAt")} fallback="A brief opening-auction phase before continuous trading — must be after Opens at." />
              </div>
              <div>
                <label className={label}>Continuous trading starts</label>
                <input
                  type="datetime-local"
                  className={fieldClass("continuousStartsAt")}
                  value={form.continuousStartsAt}
                  onBlur={() => touch("continuousStartsAt")}
                  onChange={(e) => set("continuousStartsAt", e.target.value)}
                />
                <FieldError message={fieldError("continuousStartsAt")} fallback="When normal buy/sell trading begins — must be after Auction starts." />
              </div>
              <div>
                <label className={label}>Closes at</label>
                <input
                  type="datetime-local"
                  className={fieldClass("closesAt")}
                  value={form.closesAt}
                  onBlur={() => touch("closesAt")}
                  onChange={(e) => set("closesAt", e.target.value)}
                />
                <FieldError message={fieldError("closesAt")} fallback="When trading stops — must be after Continuous trading starts." />
              </div>
              <div>
                <label className={label}>Settlement deadline</label>
                <input
                  type="datetime-local"
                  className={fieldClass("settlementDeadlineAt")}
                  value={form.settlementDeadlineAt}
                  onBlur={() => touch("settlementDeadlineAt")}
                  onChange={(e) => set("settlementDeadlineAt", e.target.value)}
                />
                <FieldError message={fieldError("settlementDeadlineAt")} fallback="The latest the result must be confirmed by — must be at or after Closes at." />
              </div>

              <div className="col-span-2 mt-2 rounded-lg bg-brand-50 p-3 dark:bg-brand-400/10">
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-500 dark:text-brand-300">Every market is Yes / No</p>
                <p className="mt-1 text-sm text-navy-700 dark:text-white">
                  Traders will be asked: <span className="font-semibold">"{form.question || "…your question above…"}"</span> — and can only answer
                  <span className="mx-1 rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-400/20 dark:text-green-300">YES</span>
                  or
                  <span className="mx-1 rounded bg-red-100 px-1.5 py-0.5 text-xs font-semibold text-red-600 dark:bg-red-400/20 dark:text-red-300">NO</span>.
                  The two fields below decide which — be exact, since this is what actually pays out.
                </p>
              </div>

              <div className="col-span-2 mt-1 text-sm font-semibold text-navy-700 dark:text-white">
                Settlement rule <span className="font-normal text-gray-400">— how this gets judged</span>
              </div>
              <div className="col-span-2">
                <label className={label}>Resolution criteria</label>
                <textarea
                  className={fieldClass("resolutionCriteria")}
                  rows={2}
                  value={form.resolutionCriteria}
                  onBlur={() => touch("resolutionCriteria")}
                  onChange={(e) => set("resolutionCriteria", e.target.value)}
                  placeholder="Resolves YES if the KNBS-published inflation rate for March 2026 exceeds 8.0%, based on the official CPI release."
                />
                <FieldError message={fieldError("resolutionCriteria")} fallback="Write this so a stranger with no other context would judge the outcome the same way you would." />
              </div>
              <div>
                <label className={label}>What is measured</label>
                <input
                  className={fieldClass("whatMeasured")}
                  value={form.whatMeasured}
                  onBlur={() => touch("whatMeasured")}
                  onChange={(e) => set("whatMeasured", e.target.value)}
                  placeholder="Kenya's year-on-year inflation rate"
                />
                <FieldError message={fieldError("whatMeasured")} fallback="The specific metric or event being asked about." />
              </div>
              <div>
                <label className={label}>When measured</label>
                <input
                  className={fieldClass("whenMeasured")}
                  value={form.whenMeasured}
                  onBlur={() => touch("whenMeasured")}
                  onChange={(e) => set("whenMeasured", e.target.value)}
                  placeholder="On publication of the March 2026 CPI report"
                />
                <FieldError message={fieldError("whenMeasured")} fallback="Exactly when the measurement or result becomes official." />
              </div>
              <div className="col-span-2">
                <label className={label}>Where sourced from</label>
                <input
                  className={fieldClass("whereSource")}
                  value={form.whereSource}
                  onBlur={() => touch("whereSource")}
                  onChange={(e) => set("whereSource", e.target.value)}
                  placeholder="Kenya National Bureau of Statistics (knbs.or.ke) official CPI release"
                />
                <FieldError message={fieldError("whereSource")} fallback="Name a specific, checkable source — not 'the news' or social media." />
              </div>
              <div>
                <label className={label}>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500 align-middle" /> YES means
                </label>
                <input
                  className={fieldClass("yesCondition")}
                  value={form.yesCondition}
                  onBlur={() => touch("yesCondition")}
                  onChange={(e) => set("yesCondition", e.target.value)}
                  placeholder="Reported rate is above 8.0%"
                />
                <FieldError message={fieldError("yesCondition")} fallback="Spell out exactly what makes this resolve YES." />
              </div>
              <div>
                <label className={label}>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500 align-middle" /> NO means
                </label>
                <input
                  className={fieldClass("noCondition")}
                  value={form.noCondition}
                  onBlur={() => touch("noCondition")}
                  onChange={(e) => set("noCondition", e.target.value)}
                  placeholder="Reported rate is 8.0% or below"
                />
                <FieldError message={fieldError("noCondition")} fallback="Spell out exactly what makes this resolve NO." />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              <p className="text-lg font-semibold text-navy-700 dark:text-white">Understand your earnings</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                The final amount you could earn from this market depends on real trading activity and the eventual result — neither is
                guaranteed, and we won't show you a projected number here because it would be a guess dressed up as a fact.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Your organization's earnings come from your share of the trading fees and commission this market actually generates once
                it's open — exactly like every other market in your portfolio. A market that never trades earns nothing. A market whose
                it's open — exactly like every other market in your portfolio. A market that never trades earns nothing.
              </p>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-lg font-semibold text-navy-700 dark:text-white">Review the creation cost</p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Live figures from the current platform policy — an admin can change or waive these at any time, so nothing here is
                hardcoded.
              </p>

              {policyLoading && <p className="mt-4 text-sm text-gray-500">Loading the current cost…</p>}

              {!policyLoading && policyError && (
                <div className="mt-4 rounded-xl bg-red-50 p-3 text-sm text-red-600 dark:bg-red-400/10">
                  {policyError}{" "}
                  <button onClick={fetchPolicy} className="font-semibold underline">Retry</button>
                </div>
              )}

              {!policyLoading && !policyError && (
                <div className="mt-4 rounded-xl bg-green-50 p-4 dark:bg-green-400/10">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300">Creating a market is free</p>
                  <p className="mt-1 text-sm text-green-700 dark:text-green-300">
                    Nothing is charged to create a market — no starting contribution and no bond. Your wallet is untouched, and the
                    market is funded entirely by the people who trade it.
                  </p>
                </div>
              )}

            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-lg font-semibold text-navy-700 dark:text-white">Keep the resolution honest</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Once trading closes you propose a result, and a second reviewer has to approve it before it can be finalized. A
                result that is rejected sends the market back so a corrected one can be proposed.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Keeping your resolution criteria and evidence sources exact (from the first step) is what makes that review quick
                and keeps your markets trusted.
              </p>
            </div>
          )}

          {step === 4 && (
            <div>
              <p className="text-lg font-semibold text-navy-700 dark:text-white">Confirm &amp; submit</p>
              <div className="mt-3 space-y-3">
                <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white">{form.question}</p>
                  <p className="mt-1 text-xs text-gray-500">{form.description}</p>
                  <p className="mt-2 text-xs text-gray-400">
                    Opens {new Date(form.opensAt).toLocaleString()} · Closes {new Date(form.closesAt).toLocaleString()} · {form.visibility}
                  </p>
                </div>
                <div className="rounded-xl border border-gray-100 p-4 dark:border-white/10">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white">Settlement rule</p>
                  <p className="mt-1 text-xs text-gray-500">{form.resolutionCriteria}</p>
                </div>
                <div className="rounded-xl bg-green-50 p-4 dark:bg-green-400/10">
                  <p className="text-sm font-semibold text-green-700 dark:text-green-300">This market will be created for free.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && <p className="mt-3 text-sm font-medium text-red-500">{error}</p>}

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          {savedAt && !recoverable && !created && (
            <span className="mr-auto text-xs text-gray-500 dark:text-gray-400">
              Saved on this device {describeAge(savedAt)}
            </span>
          )}
          <button onClick={goBack} disabled={busy} className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 dark:border-white/10 dark:text-white">
            {step === 0 ? "Cancel" : "Back"}
          </button>
          {!created && !isPartner && (
            <button
              onClick={saveAsDraft}
              disabled={draftBusy || busy || !form.question.trim()}
              title={form.question.trim() ? "Keep this for later without creating a market or being charged" : "Write a question first"}
              className="rounded-sm border border-gray-200 px-4 py-2 text-sm font-semibold text-navy-700 disabled:opacity-50 dark:border-white/10 dark:text-white"
            >
              {draftBusy ? "Saving…" : "Save as draft"}
            </button>
          )}
          {step < STEP_LABELS.length - 1 ? (
            <button
              onClick={goNext}
              disabled={step === 2 && !costReady}
              className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              Next
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={busy || !isFormComplete || !costReady}
              className="rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create Draft — free"}
            </button>
          )}
        </div>
    </div>
  );
}
